import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  output,
  viewChild,
  input,
  inject,
  effect,
  untracked,
  signal,
  viewChildren,
  linkedSignal,
} from '@angular/core';
import { FormControl, FormsModule, NgModel, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrDatagrid, ClrForm } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { Subject, firstValueFrom } from 'rxjs';
import { DynamicStyleDirective } from 'src/app/directives/dynamic-style.directive';
import { Row, Table } from 'src/app/models/ui/table';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { MrUnitTextPipe } from 'src/app/pipes/mrUnitText.pipe';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { VerwaltungsView, WartungsItem, WartungsMaske, WartungsMaskeItem, WartungsZuordnung } from '../wartung.types';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { FormGroupOfType } from 'src/app/types/shared';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { FILTER_TABLE_STORE, provideFilterStore } from 'src/app/shared/stores/filter-table.store';
import { ColumnFilterComponent } from '../../../filter/column-filter/column-filter.component';
import { ConfirmService } from 'src/app/services/confirm.service';
import { IconDgCellComponent } from '../../../_shared/datagrid-cell/icon-dg-cell.component';
import { DeselectSingleSelectedDirective } from 'src/app/directives/deselect-single-selected.directive';

interface Objekt extends Row {
  OSTAMMID: number;
}

@Component({
  selector: 'app-wartungsobjektzuordnung',
  templateUrl: './wartungsobjektzuordnung.component.html',
  styleUrls: ['./wartungsobjektzuordnung.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    MrTranslatePipe,
    MrUnitTextPipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    BackendLocaleDatePipe,
    DeselectSingleSelectedDirective,
    IconDgCellComponent,
    DynamicStyleDirective,
    ColumnFilterComponent,
  ],
  providers: [provideFilterStore()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WartungsObjektZuordnungComponent {
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly store = inject(BasicStoreService);
  private readonly api = inject(WartungApiService);
  private readonly confirmService = inject(ConfirmService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);
  protected readonly filterStore = inject(FILTER_TABLE_STORE);

  private readonly filters = viewChildren(ColumnFilterComponent);
  private readonly zuordnungsGrid = viewChild<ClrDatagrid<WartungsZuordnung>>('zuordnungsGrid');
  private readonly dateModel = viewChild('dateModel', { read: NgModel });
  private readonly clrForm = viewChild(ClrForm);

  readonly otypid = input.required<number>();
  readonly refresh = input<Subject<void>>(undefined);
  readonly view = output<VerwaltungsView>();
  protected readonly alert = signal<VerwaltungsView>(undefined);

  protected readonly personalListe = signal<WartungsItem[]>(undefined);
  protected readonly maskenListe = signal<WartungsMaske[]>(undefined);
  protected readonly maskenItems = signal<WartungsMaskeItem[]>(undefined);
  //#region objekte-Table
  protected readonly objekteTable = signal<Table<Objekt>>(undefined);
  protected readonly selectedObjekte = linkedSignal<boolean, Objekt[]>({
    source: () => this.isMultiMode(),
    computation: (multi, prev) =>
      prev ?
        multi ? prev.value
        : [prev.value[0]]
      : [],
  });
  protected readonly isMultiMode = signal(false);
  //#endregion
  //#region verknüpfungen
  protected readonly zuordnungenTable = signal<Table<WartungsZuordnung>>(undefined);
  protected readonly selectedZuordnung = signal<WartungsZuordnung>(undefined);

  protected zuordnungFormGroup = new FormGroupOfType<WartungsZuordnungForm>({
    mID: new FormControl(null, [Validators.required]),
    intervall: new FormControl(null, [Validators.required, Validators.min(0)]),
    einheit: new FormControl('Tag', { nonNullable: true }),
    pID: new FormControl(null, [Validators.required]),
    intervallbeginn: new FormControl('', [Validators.required]) /* get; set; as dateControl*/,
  });
  protected readonly insertItem = signal(false);
  //#endregion
  constructor() {
    this.filterStore.registerFilters(this.filters);
    this.filterStore.registerTable(this.objekteTable);
    effect(() => {
      if (this.otypid())
        untracked(async () => {
          const masken = await firstValueFrom(this.api.getWartungsItem<WartungsMaske>('masken-names', this.otypid()));
          this.maskenListe.set(masken.rows);

          if (this.maskenListe().length) this.loadPersonal();
          else this.alert.set('masken');

          this.selectedObjekte.set([]);
          this.objekteTable.set(null);
          this.maskenItems.set(null);
        });
    });
    effect(() => {
      const value = this.selectedObjekte();
      untracked(() => {
        this.setZuordnungTable();
        if (!value.length || this.selectedZuordnung()) {
          this.resetZuordnungStatus();
        }
      });
    });
    effect(() => {
      const value = this.selectedZuordnung();
      if (value)
        untracked(() => {
          this.insertItem.set(false);
          this.zuordnungFormGroup.enable();
          this.zuordnungFormGroup.patchValue(value);
          this.clrForm().markAsTouched();
        });
    });
  }
  //#region load()
  private async loadPersonal() {
    const personal = await firstValueFrom(this.api.getWartungsItem<WartungsItem>('personal', this.otypid()));

    this.personalListe.set(personal.rows);
    if (this.personalListe().length) this.loadObjekte();
    else this.alert.set('personal');
  }

  private async loadObjekte() {
    const table = (await firstValueFrom(this.api.getWartungsObjekte(this.otypid()))) as unknown as Table<Objekt>;
    this.objekteTable.set(table);
    this.alert.set(null);
  }

  private async setZuordnungTable() {
    const table =
      this.selectedObjekte().length == 1 && this.selectedObjekte()[0] ?
        await firstValueFrom(this.api.getWartungsZuordnungen(this.selectedObjekte()[0].OSTAMMID))
      : null;
    this.zuordnungenTable.set(table);
  }
  //#endregion
  //#region verknüpfungen()
  protected addItem() {
    this.insertItem.set(true);
    this.zuordnungsGrid()?.selection.clearSelection();
    this.zuordnungFormGroup.enable();
    this.dateModel()?.reset();
    this.zuordnungFormGroup.reset({ intervallbeginn: BackendLocaleDatePipe.today });
  }

  protected saveIfValid() {
    this.clrForm().markAsTouched();
    if (this.zuordnungFormGroup.valid) this.saveItem();
  }

  private async saveItem() {
    const value = this.zuordnungFormGroup.getRawValue();
    const sendObj = {
      ID: this.selectedZuordnung()?.ID,
      objekte: this.selectedObjekte().map(obj => obj.OSTAMMID),
      ...value,
    };

    const { success, error, ...neuRow } = await firstValueFrom(this.api.setWartungsZuordnungen(sendObj));

    if (success) {
      this.refresh()?.next();
      this.store.deleteComponentStoreAsync('wartung');
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      if (this.selectedObjekte().length == 1) {
        const neuItem: WartungsZuordnung = {
          ...neuRow,
          ...value,
          maske: this.maskenListe().find(maske => maske.ID == sendObj.mID).Name,
          verantwortlich: this.personalListe().find(pers => pers.ID == sendObj.pID).Bezeichnung,
        };
        if (this.insertItem()) this.zuordnungenTable.update(table => (table.rows.unshift(neuItem), { ...table }));
        else
          this.zuordnungenTable.update(
            table => (table.rows.splice(table.rows.indexOf(this.selectedZuordnung()), 1, neuItem), { ...table })
          );
        this.zuordnungsGrid().selected = [neuItem];
      } else this.resetZuordnungStatus();
    } else {
      this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteItem() {
    const text = this.mrTranslate.transform('Eintrag wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (!ok) return;

    const { success, error } = await firstValueFrom(
      this.api.deleteWartungsItem('zuordnung', this.selectedZuordnung().ID)
    );

    if (success) {
      this.refresh()?.next();
      this.store.deleteComponentStoreAsync('wartung');
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.zuordnungenTable.update(
        table => (table.rows.splice(table.rows.indexOf(this.selectedZuordnung()), 1), { ...table })
      );
      this.resetZuordnungStatus();
    } else {
      this.toastr.error(error, this.mrTranslate.transform('Fehler beim Löschen'));
    }
  }
  //#endregion
  //#region maske()
  protected async showMaskeDetail(id: number) {
    const arten = await firstValueFrom(this.api.getWartungsMaskeList(id));
    this.maskenItems.set(arten);
  }

  protected useMaskeInForm(id: number) {
    this.zuordnungFormGroup.get('mID').setValue(id);
  }
  //#endregion
  //#region helpers()
  private resetZuordnungStatus() {
    this.zuordnungsGrid()?.selection.clearSelection();
    this.zuordnungFormGroup.disable();
    this.zuordnungFormGroup.reset();
    this.dateModel()?.reset();
    this.insertItem.set(false);
  }

  protected get dateControl(): string {
    return this.zuordnungFormGroup.get('intervallbeginn').value;
  }
  protected set dateControl(value: string) {
    this.zuordnungFormGroup.get('intervallbeginn').setValue(value);
  }
  //#endregion
}

interface WartungsZuordnungForm {
  mID: number;
  intervall: number;
  einheit: 'Tag' | 'Monat';
  pID: number;
  intervallbeginn: string;
}

export interface WartungsZuordnungModel extends WartungsZuordnungForm {
  ID: number;
  objekte: number[];
}
